'use client';

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CaretLeftIcon,
  FileIcon,
  FilesIcon,
  FunnelXIcon,
} from '@phosphor-icons/react';
import { FeaturesContext } from '@/app/contexts';
import type { ScopesObject } from '@/shared/types';
import {
  ScopeByFileTypesChipGrid,
  applyEvidenceCategoryScopeCycle,
  evidenceCategoryScopeFieldsWithWireTokens,
} from './evidence-locker-ui-shared-new';
import {
  emptyScopesNew,
  useChatEvidenceLockerNew,
} from './chat-evidence-locker-context-new';
import { Button } from '@tranquility-ai/theme-components/button';
import { useTimePilotT } from '@/i18n/useTimePilotT';

export function hasConcreteEvidenceSelection(scopesAndFiles: ScopesObject): boolean {
  return (
    (scopesAndFiles.files?.length ?? 0) > 0 ||
    (scopesAndFiles.scopes?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedFiles?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedScopes?.length ?? 0) > 0
  );
}

/** True when the chat composer has evidence scope filters applied (not locker browse UI). */
export function hasEvidenceFilterActive(scopesAndFiles: ScopesObject): boolean {
  return (
    hasConcreteEvidenceSelection(scopesAndFiles) ||
    (scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedEvidenceCategoryScopes?.length ?? 0) > 0 ||
    !!scopesAndFiles.evidenceAllFileTypes
  );
}

type PickerView = 'menu' | 'fileType';

export type EvidenceFilterPickerMenuPlacement = 'above' | 'below';

export type EvidenceFilterPickerNewProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenEvidenceLocker: () => void;
  /** Clears scopes and filter state; defaults to context reset when omitted. */
  onClearAllFilters?: () => void;
  /**
   * `above` opens over the trigger. `below` opens under it. The menu is portaled
   * to `document.body` so it is not clipped by ancestor `overflow` containers.
   */
  menuPlacement?: EvidenceFilterPickerMenuPlacement;
  children: React.ReactNode;
};

export function EvidenceFilterPickerNew({
  isOpen,
  onOpenChange,
  onOpenEvidenceLocker,
  onClearAllFilters,
  menuPlacement = 'above',
  children,
}: EvidenceFilterPickerNewProps) {
  const t = useTimePilotT();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
    visibility: 'hidden',
  });
  const [view, setView] = useState<PickerView>('menu');
  const { scopesAndFiles, setScopesAndFiles, setShowFolders } =
    useChatEvidenceLockerNew();
  const featuresContext = useContext(FeaturesContext);
  const contentCategoriesEnabled = !!featuresContext?.featureFlags?.contentCategories;

  const selectedContentCategories = scopesAndFiles.evidenceCategoryScopes ?? [];
  const excludedContentCategories =
    scopesAndFiles.excludedEvidenceCategoryScopes ?? [];

  const onToggleCategory = useCallback(
    (category: string) => {
      setScopesAndFiles((prev) => ({
        ...prev,
        ...applyEvidenceCategoryScopeCycle(prev, category),
      }));
    },
    [setScopesAndFiles],
  );

  const closePicker = useCallback(() => {
    onOpenChange(false);
    setView('menu');
  }, [onOpenChange]);

  const updateMenuPosition = useCallback(() => {
    const anchor = rootRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;

    const rect = anchor.getBoundingClientRect();
    const gap = 8;
    const menuHeight = menu.offsetHeight;
    const top =
      menuPlacement === 'below'
        ? rect.bottom + gap
        : rect.top - menuHeight - gap;

    setMenuStyle({
      position: 'fixed',
      top,
      left: rect.left,
      zIndex: 100002,
      visibility: 'visible',
    });
  }, [menuPlacement]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle({ visibility: 'hidden' });
      return;
    }
    updateMenuPosition();
  }, [isOpen, view, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const root = rootRef.current;
      const menu = menuRef.current;
      if (
        (root && root.contains(target)) ||
        (menu && menu.contains(target))
      ) {
        return;
      }
      closePicker();
    };
    document.addEventListener('keydown', onKeyDown);
    // Defer so the opening click does not immediately hit the outside listener.
    const outsideListenerTimer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(outsideListenerTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [isOpen, closePicker]);

  const handleChooseFileOrFolder = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      closePicker();
      onOpenEvidenceLocker();
    },
    [closePicker, onOpenEvidenceLocker],
  );

  const handleChooseFileType = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setView('fileType');
  }, []);

  const handleClearAllFilters = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClearAllFilters) {
        onClearAllFilters();
      } else {
        setScopesAndFiles(emptyScopesNew());
        setShowFolders(false);
      }
      closePicker();
    },
    [closePicker, onClearAllFilters, setScopesAndFiles, setShowFolders],
  );

  const menuPanel = isOpen ? (
    <div
      ref={menuRef}
      role="dialog"
      aria-label={t('chat.evidence.filterOptions')}
      style={menuStyle}
      className="overflow-hidden rounded-lg border border-black/10 bg-white shadow-lg"
      data-evidence-filter-picker-menu
    >
      {view === 'menu' ? (
        <div className="flex min-w-[220px] flex-col py-1">
          <Button
            type="button"
            style="text"
            color="subdued"
            background="default"
            size="sm"
            className="w-full h-auto justify-start gap-3 px-4 py-3 text-left text-sm"
            leadingIcon={<FileIcon className="shrink-0 text-brand-blue" />}
            onClick={handleChooseFileOrFolder}
          >
            {t('chat.evidence.fileOrFolder')}
          </Button>
          {contentCategoriesEnabled ? (
            <Button
              type="button"
              style="text"
              color="subdued"
              background="default"
              size="sm"
              className="w-full h-auto justify-start gap-3 px-4 py-3 text-left text-sm"
              leadingIcon={<FilesIcon className="shrink-0 text-brand-blue" />}
              onClick={handleChooseFileType}
            >
              {t('chat.evidence.fileType')}
            </Button>
          ) : null}
          <div aria-hidden className="mx-3 border-t border-black/10" />
          <Button
            type="button"
            style="text"
            color="subdued"
            background="default"
            size="sm"
            className="w-full h-auto justify-start gap-3 px-4 py-3 text-left text-sm"
            leadingIcon={<FunnelXIcon className="shrink-0 text-brand-blue" />}
            onClick={handleClearAllFilters}
          >
            {t('chat.evidence.clearAllFilters')}
          </Button>
        </div>
      ) : (
        <div className="flex w-[min(92vw,280px)] flex-col gap-2 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              <Button
                type="button"
                iconOnly
                size="sm"
                style="text"
                color="subdued"
                background="default"
                aria-label={t('common.back')}
                className="shrink-0 text-black/60"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setView('menu');
                }}
              >
                <CaretLeftIcon />
              </Button>
              <span className="truncate text-sm font-medium text-black">{t('chat.evidence.fileTypes')}</span>
            </div>
            <Button
              type="button"
              style="solid"
              color="accent"
              background="default"
              size="sm"
              className="shrink-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closePicker();
              }}
            >
              {t('common.done')}
            </Button>
          </div>
          <ScopeByFileTypesChipGrid
            selectedContentCategories={selectedContentCategories}
            excludedContentCategories={excludedContentCategories}
            onToggleCategory={onToggleCategory}
          />
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={rootRef} id="evidence-filter-picker-root" className="relative">
      {children}
      {typeof document !== 'undefined' && menuPanel
        ? createPortal(menuPanel, document.body)
        : null}
    </div>
  );
}
